import * as React from "react";
import { FloatingPortal } from "@floating-ui/react";
import { cn } from "@/lib/utils";
import { IMenuEntry } from "../UI/NoxusMenu";
import { Separator } from "../shadcn/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";

export function RowContextMenu({
  items,
  setFloating,
  floatingStyles,
  onClose,
  root,
}: {
  items: IMenuEntry[];
  setFloating: (node: HTMLElement | null) => void;
  floatingStyles: React.CSSProperties;
  onClose: () => void;
  root?: HTMLElement | null;
}) {
  return (
    <FloatingPortal root={root ?? undefined}>
      <div
        ref={setFloating}
        style={floatingStyles}
        className="z-50"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          data-testid="row-menu"
          className="flex flex-col overflow-hidden min-w-32 bg-background z-50 max-h-(--radix-dropdown-menu-content-available-height) origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md"
        >
          {items.map((item, index) => {
            if (item.kind === "separator") {
              return (
                <Separator key={`menu-sep-${index}`} className="-mx-1 my-1" />
              );
            }
            if (item.kind === "label") {
              return (
                <div
                  key={`menu-label-${index}`}
                  className="px-2 py-1.5 text-xs text-muted-foreground"
                >
                  {item.label}
                </div>
              );
            }
            const row = (
              <div
                key={`menu-item-${index}`}
                className={cn(
                  "w-full flex gap-2 p-2 items-center focus:bg-accent focus:text-accent-foreground",
                  "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                  item.disabled
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:cursor-pointer hover:bg-accent",
                  item.color?.includes("destructive") &&
                    !item.disabled &&
                    `text-destructive`
                )}
                onClick={() => {
                  if (item.disabled) return;
                  item.onClick?.();
                  onClose();
                }}
              >
                {item.icon && (
                  <item.icon
                    size="1rem"
                    strokeWidth="1.33px"
                    color={
                      item.color === "destructive" && !item.disabled
                        ? "var(--destructive)"
                        : undefined
                    }
                  />
                )}
                <p data-testid="row-menu-item" className="text-sm">
                  {item.label}
                </p>
              </div>
            );
            if (item.disabled && item.tooltip) {
              return (
                <Tooltip key={`menu-item-${index}`}>
                  <TooltipTrigger asChild>{row}</TooltipTrigger>
                  <TooltipContent side="left">{item.tooltip}</TooltipContent>
                </Tooltip>
              );
            }
            return row;
          })}
        </div>
      </div>
    </FloatingPortal>
  );
}
